import React, { useState, useEffect } from 'react';
import { useOrderFlowStore } from '../../../store/orderFlowStore';
import { useTranslations } from '../../../hooks/useTranslations';
import { useOptionIcons } from '../../../hooks/useOptionIcons';
import { useTemporaryRestrictions } from '../../../hooks/useTemporaryRestrictions';
import FileUpload from '../../../components/molecules/FileUpload/FileUpload';
import HelpButton from '../../../components/atoms/HelpButton/HelpButton';

import styles from './DesignSubmission.module.scss';

export const DesignSubmission: React.FC = () => {
  const t = useTranslations();
  const { getOptionIcon, isOptionIconsAvailable } = useOptionIcons();
  const { 
    productType,
    packageType,
    beanType,
    selectedBean,
    lotSize,
    sugarConcentration,
    needsOuterBox,
    needsInnerDesign,
    needsFrontLabel,
    needsBackLabel,
    userSelections,
    setNeedsBackLabel,
    outerBoxDesignFile,
    setOuterBoxDesignFile,
    innerDesignFile,
    setInnerDesignFile,
    frontLabelFile,
    setFrontLabelFile,
    backLabelFile,
    setBackLabelFile,
    totalPrice,
    basePrice,
    optionPrices,
    calculateTotalPrice
  } = useOrderFlowStore();

  const { isBackLabelRequiredDisabled, disabledMessage } = useTemporaryRestrictions();

  // 裏ラベルの選択状態を管理（初期状態では未選択）
  const [backLabelSelected, setBackLabelSelected] = useState<'required' | 'not-required' | null>(null);

  // ストアの状態と同期（ユーザーが選択を行った場合のみ）
  useEffect(() => {
    if (userSelections.hasSelectedBackLabel) {
      setBackLabelSelected(needsBackLabel ? 'required' : 'not-required');
    }
  }, [needsBackLabel, userSelections.hasSelectedBackLabel]);

  // 裏ラベル「必要」非活性時の自動選択
  useEffect(() => {
    if (isBackLabelRequiredDisabled) {
      if (needsBackLabel || !userSelections.hasSelectedBackLabel) {
        setNeedsBackLabel(false);
      }
    }
  }, [isBackLabelRequiredDisabled, needsBackLabel, userSelections.hasSelectedBackLabel, setNeedsBackLabel]);

  // デザイン要件の判定（STEP3の選択に基づく）
  const shouldShowOuterBox = needsOuterBox;
  const shouldShowInnerDesign = needsInnerDesign;
  const shouldShowFrontLabel = needsFrontLabel;

  // 裏ラベル要否の選択処理
  const handleBackLabelChange = (required: boolean) => {
    setNeedsBackLabel(required);
  };





  return (
    <div className={styles.designSubmission}>
      {/* メインコンテンツエリア */}
      <div className={styles.mainContent}>
        
        {/* 外箱デザインテンプレート選択（外箱が必要な場合のみ） */}
        {shouldShowOuterBox && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t.designSubmission_outerBoxTemplateTitle}</h2>
              <HelpButton helpText={t.help_outerBoxDesignTemplate || ''} />
            </div>
            
            <div className={styles.templateOptions}>
              <div className={styles.templateIcon}></div>
              <div className={styles.templateContent}>
                <div className={styles.templateRow}>
                  <span>{t.designSubmission_originalDesignCase}</span>
                  <a 
                    className={styles.downloadLink}
                    href="https://drive.google.com/drive/folders/1K8JLEBybETcJjhWRGZCDELKFtRArQGFG?usp=drive_link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.designSubmission_templateDownload}
                  </a>
                </div>
                <div className={styles.templateRow}>
                  <span>{t.designSubmission_companyDesignCase}</span>
                  <a 
                    className={styles.downloadLink}
                    href="https://drive.google.com/drive/folders/1K8JLEBybETcJjhWRGZCDELKFtRArQGFG?usp=drive_link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.designSubmission_dataDownload}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 外箱デザインアップロード（外箱が必要な場合のみ） */}
        {shouldShowOuterBox && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t.designSubmission_outerBoxUploadTitle}</h2>
              <HelpButton helpText={t.help_outerBoxDesignUpload || ''} />
            </div>
            
            <div className={styles.uploadWrapper}>
              <FileUpload
                onFileSelect={(file) => setOuterBoxDesignFile(file)}
                selectedFile={outerBoxDesignFile}
                label={t.file_outerBoxDesignFile}
                accept=".jpg,.jpeg,.png,.pdf,.ai"
              />
            </div>
          </div>
        )}

        {/* インナーパッケージデザインテンプレート選択（インナーデザインが必要な場合のみ） */}
        {shouldShowInnerDesign && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t.designSubmission_innerPackageTemplateTitle}</h2>
              <HelpButton helpText={t.help_innerPackageDesignTemplate || ''} />
            </div>
            
            <div className={styles.templateOptions}>
              <div className={styles.templateIcon}></div>
              <div className={styles.templateContent}>
                <div className={styles.templateRow}>
                  <span>{t.designSubmission_originalDesignCase}</span>
                  <a 
                    className={styles.downloadLink}
                    href="https://drive.google.com/drive/folders/1K8JLEBybETcJjhWRGZCDELKFtRArQGFG?usp=drive_link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.designSubmission_templateDownload}
                  </a>
                </div>
                <div className={styles.templateRow}>
                  <span>{t.designSubmission_companyDesignCase}</span>
                  <a 
                    className={styles.downloadLink}
                    href="https://drive.google.com/drive/folders/1K8JLEBybETcJjhWRGZCDELKFtRArQGFG?usp=drive_link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.designSubmission_dataDownload}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* インナーパッケージデザインアップロード（インナーデザインが必要な場合のみ） */}
        {shouldShowInnerDesign && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t.designSubmission_innerPackageUploadTitle}</h2>
              <HelpButton helpText={t.help_innerPackageDesignUpload || ''} />
            </div>
            
            <div className={styles.uploadWrapper}>
              <FileUpload
                onFileSelect={(file) => setInnerDesignFile(file)}
                selectedFile={innerDesignFile}
                label={t.design_innerDesignFile}
                accept=".jpg,.jpeg,.png,.pdf,.ai"
              />
            </div>
          </div>
        )}

        {/* 表ラベルデザインテンプレート選択（フリーズドライキューブの場合のみ） */}
        {shouldShowFrontLabel && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t.designSubmission_frontLabelTemplateTitle}</h2>
              <HelpButton helpText={t.help_frontLabelDesignTemplate || ''} />
            </div>
            
            <div className={styles.templateOptions}>
              <div className={styles.templateIcon}></div>
              <div className={styles.templateContent}>
                <div className={styles.templateRow}>
                  <span>{t.designSubmission_originalDesignCase}</span>
                  <a 
                    className={styles.downloadLink}
                    href="https://drive.google.com/drive/folders/1K8JLEBybETcJjhWRGZCDELKFtRArQGFG?usp=drive_link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.designSubmission_templateDownload}
                  </a>
                </div>
                <div className={styles.templateRow}>
                  <span>{t.designSubmission_companyDesignCase}</span>
                  <a 
                    className={styles.downloadLink}
                    href="https://drive.google.com/drive/folders/1K8JLEBybETcJjhWRGZCDELKFtRArQGFG?usp=drive_link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.designSubmission_dataDownload}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 表ラベルデザインアップロード（フリーズドライキューブの場合のみ） */}
        {shouldShowFrontLabel && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t.designSubmission_frontLabelUploadTitle}</h2>
              <HelpButton helpText={t.help_frontLabelDesignUpload || ''} />
            </div>
            
            <div className={styles.uploadWrapper}>
              <FileUpload
                onFileSelect={(file) => setFrontLabelFile(file)}
                selectedFile={frontLabelFile}
                label={t.design_frontLabelFile}
                accept=".jpg,.jpeg,.png,.pdf,.ai"
              />
            </div>
          </div>
        )}

        {/* 裏ラベル（一括表示）*/}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t.designSubmission_backLabelBulkDisplayTitle}</h2>
            <HelpButton helpText={t.help_backLabelSelection || ''} />
          </div>
          
          <div className={styles.radioSection}>
            {/* 必要を選択 */}
            <div className={`${styles.radioOption} ${isBackLabelRequiredDisabled ? styles.radioOptionDisabled : ''}`}>
              <label className={`${styles.radioLabel} ${isBackLabelRequiredDisabled ? styles.radioLabelDisabled : ''}`}>
                <input
                  type="radio"
                  id="backLabelRequired"
                  name="backLabel"
                  checked={backLabelSelected === 'required'}
                  onChange={() => handleBackLabelChange(true)}
                  className={styles.radioInput}
                  disabled={isBackLabelRequiredDisabled}
                />
                <span className={styles.customRadio} />
                {isOptionIconsAvailable() && getOptionIcon('back', 'label-required') && (
                  <img
                    src={getOptionIcon('back', 'label-required')}
                    alt={t.design_backLabelRequired}
                    className={styles.optionImage}
                  />
                )}
                <div className={styles.radioContent}>
                  <div className={styles.radioTitle}>{t.designSubmission_required}</div>
                  <div className={styles.radioDescription}>
                    {t.designSubmission_requiredDescription}
                  </div>
                  {isBackLabelRequiredDisabled && (
                    <div className={styles.disabledMessage}>{disabledMessage}</div>
                  )}
                </div>
              </label>

              {/* 必要選択時の追加コンテンツ */}
              {backLabelSelected === 'required' && (
                <div className={styles.nestedContent}>
                  {/* 裏ラベルテンプレート選択 */}
                  <div className={styles.nestedSection}>
                    <div className={styles.nestedHeader}>
                      <h3 className={styles.nestedTitle}>{t.designSubmission_backLabelTemplateSelectionTitle}</h3>
                      <HelpButton helpText={t.help_backLabelDesignTemplate || ''} />
                    </div>
                    <a 
                      className={styles.downloadLink}
                      href="https://drive.google.com/drive/folders/1K8JLEBybETcJjhWRGZCDELKFtRArQGFG?usp=drive_link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t.designSubmission_templateDownload}
                    </a>
                  </div>

                  {/* 裏ラベルアップロード */}
                  <div className={styles.nestedSection}>
                    <div className={styles.nestedHeader}>
                      <h3 className={styles.nestedTitle}>{t.designSubmission_backLabelUploadTitle}</h3>
                      <HelpButton helpText={t.help_backLabelDesignUpload || ''} />
                    </div>
                    
                    <div className={styles.uploadWrapper}>
                      <FileUpload
                        onFileSelect={(file) => setBackLabelFile(file)}
                        selectedFile={backLabelFile}
                        label={t.design_backLabelFile}
                        accept=".jpg,.jpeg,.png,.pdf,.ai"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 不要を選択 */}
            <div className={styles.radioOption}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  id="backLabelNotRequired"
                  name="backLabel"
                  checked={backLabelSelected === 'not-required'}
                  onChange={() => handleBackLabelChange(false)}
                  className={styles.radioInput}
                />
                <span className={styles.customRadio} />
                {isOptionIconsAvailable() && getOptionIcon('back', 'label-not-required') && (
                  <img 
                    src={getOptionIcon('back', 'label-not-required')} 
                    alt={t.design_backLabelNotRequired} 
                    className={styles.optionImage}
                  />
                )}
                <div className={styles.radioContent}>
                  <div className={styles.radioTitle}>{t.designSubmission_notRequired}</div>
                  <div className={styles.radioDescription}>
                    {t.designSubmission_notRequiredDescription}
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
}; 